import type {
  ElementLoginResponse,
  ElementMailbox,
  ElementMessage,
  ElementMessageListResponse,
  ElementFolder,
  ElementDomain,
  ElementDnsRecord,
  ElementSendEmailRequest,
  ElementBulkAction
} from '../types'

const ELEMENT_AUTH_WORKER_URL = import.meta.env.VITE_AUTH_WORKER_URL as string
const ELEMENT_API_WORKER_URL = import.meta.env.VITE_API_WORKER_URL as string

function getStoredElementAuthToken(): string | null {
  return localStorage.getItem('element_auth_token')
}

function buildAuthorizationHeader(): Record<string, string> {
  const storedToken = getStoredElementAuthToken()
  return storedToken ? { Authorization: `Bearer ${storedToken}` } : {}
}

async function sendElementAuthRequest<ResponseType>(
  endpointPath: string,
  requestOptions: RequestInit = {}
): Promise<ResponseType> {
  const fullRequestUrl = `${ELEMENT_AUTH_WORKER_URL}${endpointPath}`
  const httpResponse = await fetch(fullRequestUrl, {
    ...requestOptions,
    headers: {
      'Content-Type': 'application/json',
      ...buildAuthorizationHeader(),
      ...requestOptions.headers
    }
  })
  if (!httpResponse.ok) {
    const errorResponseBody = await httpResponse.json() as { error?: string }
    throw new Error(errorResponseBody.error || `Auth request failed with status ${httpResponse.status}`)
  }
  return httpResponse.json() as Promise<ResponseType>
}

async function sendElementApiRequest<ResponseType>(
  endpointPath: string,
  requestOptions: RequestInit = {}
): Promise<ResponseType> {
  const fullRequestUrl = `${ELEMENT_API_WORKER_URL}${endpointPath}`
  const httpResponse = await fetch(fullRequestUrl, {
    ...requestOptions,
    headers: {
      'Content-Type': 'application/json',
      ...buildAuthorizationHeader(),
      ...requestOptions.headers
    }
  })
  if (!httpResponse.ok) {
    const errorResponseBody = await httpResponse.json() as { error?: string }
    throw new Error(errorResponseBody.error || `API request failed with status ${httpResponse.status}`)
  }
  return httpResponse.json() as Promise<ResponseType>
}

// ── AUTH API ──

export async function loginToElementMail(
  emailAddress: string,
  plaintextPassword: string
): Promise<ElementLoginResponse> {
  return sendElementAuthRequest<ElementLoginResponse>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email: emailAddress, password: plaintextPassword })
  })
}

export async function logoutOfElementMail(): Promise<void> {
  await sendElementAuthRequest('/auth/logout', { method: 'POST' })
  localStorage.removeItem('element_auth_token')
}

export async function fetchAuthenticatedMailbox(): Promise<ElementMailbox> {
  return sendElementAuthRequest<ElementMailbox>('/auth/me')
}

// ── MESSAGES API ──

export interface ElementMessageListQueryParams {
  folder?: string
  page?: number
  limit?: number
  search?: string
  unread?: boolean
  starred?: boolean
}

export async function fetchElementMessageList(
  queryParams: ElementMessageListQueryParams = {}
): Promise<ElementMessageListResponse> {
  const urlSearchParams = new URLSearchParams()
  if (queryParams.folder) urlSearchParams.set('folder', queryParams.folder)
  if (queryParams.page) urlSearchParams.set('page', String(queryParams.page))
  if (queryParams.limit) urlSearchParams.set('limit', String(queryParams.limit))
  if (queryParams.search) urlSearchParams.set('search', queryParams.search)
  if (queryParams.unread) urlSearchParams.set('unread', 'true')
  if (queryParams.starred) urlSearchParams.set('starred', 'true')

  return sendElementApiRequest<ElementMessageListResponse>(
    `/messages?${urlSearchParams.toString()}`
  )
}

export async function fetchElementMessageById(
  messageId: string
): Promise<ElementMessage> {
  return sendElementApiRequest<ElementMessage>(`/messages/${messageId}`)
}

export async function fetchElementMessageBody(
  messageId: string
): Promise<string> {
  const storedToken = getStoredElementAuthToken()
  const rawEmailResponse = await fetch(
    `${ELEMENT_API_WORKER_URL}/messages/${messageId}/body`,
    {
      headers: storedToken ? { Authorization: `Bearer ${storedToken}` } : {}
    }
  )
  if (!rawEmailResponse.ok) throw new Error('Failed to fetch message body')
  return rawEmailResponse.text()
}

export async function updateElementMessage(
  messageId: string,
  messageUpdates: {
    isRead?: boolean
    isStarred?: boolean
    targetFolder?: string
  }
): Promise<void> {
  await sendElementApiRequest(`/messages/${messageId}`, {
    method: 'PATCH',
    body: JSON.stringify(messageUpdates)
  })
}

export async function deleteElementMessage(messageId: string): Promise<void> {
  await sendElementApiRequest(`/messages/${messageId}`, { method: 'DELETE' })
}

export async function bulkUpdateElementMessages(
  messageIds: string[],
  bulkAction: ElementBulkAction,
  targetFolder?: string
): Promise<void> {
  await sendElementApiRequest('/messages/bulk', {
    method: 'POST',
    body: JSON.stringify({ messageIds, bulkAction, targetFolder })
  })
}

// ── FOLDERS API ──

export async function fetchElementFolderList(): Promise<ElementFolder[]> {
  const folderListResponse = await sendElementApiRequest<{ folders: ElementFolder[] }>('/folders')
  return folderListResponse.folders
}

// ── DOMAINS API ──

export async function fetchElementDomainList(): Promise<ElementDomain[]> {
  const domainListResponse = await sendElementApiRequest<{ domains: ElementDomain[] }>('/domains')
  return domainListResponse.domains
}

export async function registerElementDomain(
  domainName: string
): Promise<{ ok: boolean; domainId: string; domain: string }> {
  return sendElementApiRequest('/domains', {
    method: 'POST',
    body: JSON.stringify({ domainName })
  })
}

export async function fetchElementDomainDnsRecords(
  domainId: string
): Promise<{ domain: string; requiredDnsRecords: ElementDnsRecord[] }> {
  return sendElementApiRequest(`/domains/${domainId}/dns-records`)
}

export async function verifyElementDomain(
  domainId: string
): Promise<{ domainIsFullyVerified: boolean; dnsChecks: Record<string, boolean> }> {
  return sendElementApiRequest(`/domains/${domainId}/verify`, { method: 'POST' })
}

// ── SEND API ──

export async function sendElementEmail(
  sendEmailRequest: ElementSendEmailRequest
): Promise<{ ok: boolean; messageId: string }> {
  return sendElementApiRequest('/send', {
    method: 'POST',
    body: JSON.stringify(sendEmailRequest)
  })
}
